import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { Image, TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import ExperienciaIcon from "@assets/icons/distribuidor - experiencia.svg";
import EficaciaIcon from "@assets/icons/distribuidor - eficacia probada.svg";
import TecnologiaIcon from "@assets/icons/distribuidor - tecnologia propia.svg";
import SoporteIcon from "@assets/icons/croptology - asesoramiento_distribuidor soporte.svg";
import { useTranslation } from "next-i18next";
import styles from "./hazte-distribuidor.module.scss";
import { NextSeo } from "next-seo";
import { settings } from "@config/settings";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { localesParserDictionary } from "@utils/locales-parser";
import { ContactForm } from "src/presentation/components/organisms";

const VentajasSection = () => {
  const { t } = useTranslation();
  const ventajas = [
    {
      key: "experiencia",
      icon: <ExperienciaIcon />,
    },
    {
      key: "eficacia",
      icon: <EficaciaIcon />,
    },
    {
      key: "tecnologia",
      icon: <TecnologiaIcon />,
    },
    {
      key: "soporte",
      icon: <SoporteIcon />,
    },
  ];
  return (
    <section className={styles.ventajasSection}>
      <Text as="h2">{t("DistribuidorView.ventajas.title")}</Text>
      <div className={styles.ventajas}>
        {ventajas.map((item) => {
          return (
            <div key={item.key} className={styles.ventaja}>
              {item.icon}
              <Text as="h3">
                {t(`DistribuidorView.ventajas.${item.key}.title`)}
              </Text>
              <TemplateText template="normal">
                {t(`DistribuidorView.ventajas.${item.key}.description`)}
              </TemplateText>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const HazteDistribuidorPage: NextPage = () => {
  const { t } = useTranslation();
  return (
    <>
      <NextSeo
        title={t("SEOMeta.DistribuidorView.title") || ""}
        description={t("SEOMeta.DistribuidorView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.distribuidor"),
            url: MainRouter["hazte-distribuidor"].generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <section className={styles.topsection}>
            <div className={styles.datacontainer}>
              <div className={styles.data}>
                <Text as="h1">{t("DistribuidorView.heading.title")}</Text>
                <TemplateText template="normal">
                  {t("DistribuidorView.heading.p1")}
                </TemplateText>
                <TemplateText template="normal">
                  {t("DistribuidorView.heading.p2")}
                </TemplateText>
                <TemplateText template="normal">
                  {t("DistribuidorView.heading.p3")}
                </TemplateText>
              </div>
            </div>
            <Image
              src={
                "/static/images/hazte-distribuidor-manvert-bioestimulantes.jpg"
              }
              alt="hazte distribuidor manvert bioestimulantes"
            />
          </section>
          <VentajasSection />
          <section className={styles.formsection}>
            <ContactForm
              title={t("DistribuidorView.formtitle") || ""}
              empresa
              provincia
              telefono
              ciudad
              comentarios
              reducedCargosList
              link="https://go.pardot.com/l/939633/2021-09-08/fpjvj5"
              GDPRTextVersion="distribuidor"
              eventAction="EnviarSolicitud"
              eventLabel="Formulario Hazte Distribuidor"
            />
          </section>
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productosDestacados: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: selectedCountry.countrycode,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default HazteDistribuidorPage;
